import {
  applicableGpcFormStages, gpcStageRequirement, allGpcFormStages, GPC_FORM_STAGE_BASE_OFFSET,
  gpcTotalStageCount, isGpcStageEnabled, lastEnabledGpcStageAtOrBelow,
  mapMfrResponse, mapPriceQuote, mapThresholdResult, normalizeDecision, restoreGpcCarpetCheck,
  mapApproval as mapGpcApproval, mapGpcNaicsPsc, mapGpcVendor, mapGpc889,
  restoreGpcAbstractFromStageOutput, restoreGpcDd2579FromStageOutput,
  mapGpcAfForm9, mapGpcTaxExempt, restoreGpcReceiptFromStageOutput, mapGpcIuid,
} from '../api/gpcFunctions';
import type {
  GpcFormStageKey, GpcPriceQuote, GpcThresholdResult, GpcBuyParams,
  GpcSourceDecision, GpcNaicsPsc, GpcSourceStatus, GpcVendor,
  Gpc889Representation, GpcAbstract, GpcDd2579, GpcAfForm9,
  GpcApproval, GpcTaxExempt, GpcReceipt, GpcIuid, GpcCarpetIiCheck,
  ApplicableGpcFormStagesArgs, GpcStageRequirement,
} from '../api/gpcFunctions';
import type { Question, MFR, Product } from '../types/pipeline';
import { resolveGpcCompletionMetadata } from '../api/gpcCompletion';
import { getStageData } from './cacheUtils';

export interface GpcRestoredState {
  rep889: Gpc889Representation | null;
  questions: Question[];
  answers: Record<string, string>;
  requirementText: string;
  mfr: MFR | null;
  priceSearchComplete: boolean;
  searchGeneration: string | null;
  priceQuotes: GpcPriceQuote[];
  unitPrice: number | null;
  threshold: GpcThresholdResult | null;
  buyParams: GpcBuyParams;
  decision: GpcSourceDecision | null;
  naicsPsc: GpcNaicsPsc | null;
  products: Product[];
  sourceStatus: Record<string, GpcSourceStatus>;
  carpetIiCheck: GpcCarpetIiCheck | null;
  selectedProductIds: string[];
  awardProductId: string;
  awardOverrideReason: string;
  vendor: GpcVendor | null;
  awardIsSmall: boolean | null;
  competitionQuotes: GpcPriceQuote[];
  abstract: GpcAbstract | null;
  dd2579: GpcDd2579 | null;
  afForm9: GpcAfForm9 | null;
  approval: GpcApproval | null;
  taxExempt: GpcTaxExempt | null;
  receipt: GpcReceipt | null;
  iuid: GpcIuid | null;
}

export interface GpcRestoreOptions {
  flowId: string;
  metadata: Record<string, unknown>;
  savedStage: string | null;
  stageHint?: GpcFormStageKey;
  isCurrentOperation: () => boolean;
  onPatch: (patch: Partial<GpcRestoredState>) => void;
}

/** Server required membership wins; local rules only support legacy flows. */
export function resolveGpcStageRequirement(
  key: GpcFormStageKey,
  args: ApplicableGpcFormStagesArgs,
  validation: Record<string, unknown> | null,
): GpcStageRequirement {
  const local = gpcStageRequirement(key, args);
  const required = resolveGpcCompletionMetadata({
    gpc_required_fields_validation: validation,
  }).requiredStages;
  if (required === null) return local;
  if (required.includes(key)) return { required: true, reason: null };
  return {
    required: false,
    reason: local.reason ?? 'Not required for this purchase; available for review.',
  };
}

export interface GpcRestoreResult {
  availableStages: GpcFormStageKey[];
  targetStage: number;
  maxStageReached: number;
  /** Best-effort hydration is not a completion verdict. */
  hydrationPartial: boolean;
}

/** Keep stable indices and never restore a disabled or unreachable form. */
export function resolveGpcLanding(
  completedStages: number[],
  availableStages: GpcFormStageKey[],
  savedStage: string | null,
  stageHint?: GpcFormStageKey,
): { targetStage: number; maxStageReached: number } {
  const formsReachable = completedStages.includes(802);
  let maxStageReached = 0;
  if (formsReachable || completedStages.includes(801)) maxStageReached = 2;
  else if (completedStages.includes(800)) maxStageReached = 1;
  const available = new Set(availableStages);
  if (formsReachable && availableStages.length) {
    maxStageReached = lastEnabledGpcStageAtOrBelow(
      gpcTotalStageCount() - 1, available,
    );
  }
  const hintedIndex = formsReachable && stageHint && available.has(stageHint)
    ? GPC_FORM_STAGE_BASE_OFFSET + allGpcFormStages().indexOf(stageHint)
    : null;
  let targetStage = hintedIndex ?? maxStageReached;
  if (hintedIndex === null && savedStage !== null) {
    const parsed = parseInt(savedStage, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= maxStageReached) {
      targetStage = parsed;
    }
  }
  if (formsReachable && !isGpcStageEnabled(targetStage, available)) {
    targetStage = lastEnabledGpcStageAtOrBelow(targetStage, available);
  }
  return { targetStage, maxStageReached: Math.max(maxStageReached, targetStage) };
}

/** Restore only the active operation; every asynchronous read has its own fence. */
export async function restoreGpcWorkflow({
  flowId, metadata, savedStage, stageHint, isCurrentOperation, onPatch,
}: GpcRestoreOptions): Promise<GpcRestoreResult | null> {
  if (!isCurrentOperation()) return null;
  const serverAvailable = resolveGpcCompletionMetadata(metadata).availableStages;
  const rawCompleted = metadata.completedStages ?? metadata.completed_stages;
  const completedStages = Array.isArray(rawCompleted)
    ? rawCompleted.filter((stage): stage is number => typeof stage === 'number')
    : [];
  let availableStages = serverAvailable ?? [];
  let hydrationPartial = false;
  let priceSearchBlocksForms = false;
  // Restore persisted GPC stage outputs into shared state (best-effort).
  try {
    const qData = await getStageData(flowId, 800);
    if (!isCurrentOperation()) return null;
    if (qData?.output) {
      if (Array.isArray(qData.output.questions)) onPatch({ questions: qData.output.questions });
      if (qData.output.answers && typeof qData.output.answers === 'object') onPatch({ answers: qData.output.answers });
    }
    if (qData?.input?.requirement_text) onPatch({ requirementText: qData.input.requirement_text });
    const mfrData = await getStageData(flowId, 801);
    if (!isCurrentOperation()) return null;
    if (mfrData?.output) {
      const raw =
        mfrData.output.requirements_text ||
        mfrData.output.minimum_requirements ||
        mfrData.output.rawText;
      if (raw) {
        // Reuse the SAME snake_case→MFR mapping as the generate path
        // (gpcFunctions.mapMfrResponse) so `removedSpecs` /
        // `costSavingsEstimate` survive a flow reopen and the
        // "Removed Overspecifications" alert renders again.
        onPatch({ mfr: mapMfrResponse(
            mfrData.output,
            qData?.input?.requirement_text || '',
            [],
            { id: `gpc-mfr-${flowId}` },
          ) });
      }
    }
    // Stage 802 now holds the searched price quotes + derived unit price
    // + the threshold result (Phase 8, [NOTES-3]).
    const prodData = await getStageData(flowId, 802);
    if (!isCurrentOperation()) return null;
    if (prodData?.output) {
      const searchMarker = prodData.output.price_search;
      const searchComplete = searchMarker?.completed === true ||
        (!searchMarker && (Boolean(prodData.output.threshold) ||
          (Array.isArray(prodData.output.price_quotes) &&
            prodData.output.price_quotes.length > 0)));
      const searchGeneration = searchComplete &&
        typeof searchMarker?.generation === 'string'
        ? searchMarker.generation : null;
      const thresholdIsCurrent = !searchGeneration ||
        prodData.output.threshold_generation === searchGeneration;
      priceSearchBlocksForms = Boolean(searchMarker?.generation) &&
        (searchMarker?.completed !== true ||
          !prodData.output.threshold || !thresholdIsCurrent);
      // `price_search.completed` is the durable server contract for a
      // completed discovery run, including the meaningful zero-result
      // case. Older saved flows use a non-empty quote list or threshold;
      // an old bare [] alone does not prove completion.
      onPatch({ priceSearchComplete: searchComplete, searchGeneration });
      if (searchComplete && Array.isArray(prodData.output.price_quotes)) {
        onPatch({ priceQuotes: prodData.output.price_quotes.map(mapPriceQuote) });
      } else if (!searchComplete) {
        onPatch({ priceQuotes: [], unitPrice: null });
      }
      if (searchComplete && prodData.output.unit_price !== undefined &&
          prodData.output.unit_price !== null) {
        onPatch({ unitPrice: prodData.output.unit_price });
      }
      if (searchComplete && thresholdIsCurrent && prodData.output.threshold) {
        onPatch({ threshold: mapThresholdResult(prodData.output.threshold) });
      } else {
        onPatch({ threshold: null });
      }
      if (searchComplete && prodData.output.buy_params) {
        const savedParams = prodData.output.buy_params;
        if (thresholdIsCurrent) {
          onPatch({ buyParams: savedParams });
        } else {
          const safeParams: GpcBuyParams = {};
          for (const key of ['requirement_title', 'quantity', 'buy_type',
            'buy_type_agent_source'] as const) {
            if (Object.prototype.hasOwnProperty.call(savedParams, key)) {
              (safeParams as any)[key] = savedParams[key];
            }
          }
          onPatch({ buyParams: safeParams });
        }
      } else if (!searchComplete) {
        const saved = prodData.output.buy_params;
        onPatch({ buyParams: saved?.buy_type &&
          saved.buy_type_agent_source === false ? {
            buy_type: saved.buy_type, buy_type_agent_source: false,
            ...(saved.quantity != null ? { quantity: saved.quantity } : {}),
          } : {} });
      }
      // Path-B mandatory-source decision persisted inline in the 802
      // record (decide endpoint stores it under output.decision). Restore
      // it so reopening a saved flow shows the decided panel directly.
      if (prodData.output.decision) {
        onPatch({ decision: normalizeDecision(prodData.output.decision) });
      }
      // Recompute the applicable per-form stage subset from the restored
      // Stage-3 outputs so the dynamic stepper's reachable length matches.
      if (!priceSearchBlocksForms && completedStages.includes(802) && serverAvailable === null) {
        const restoredThreshold = searchComplete && thresholdIsCurrent && prodData.output.threshold
          ? mapThresholdResult(prodData.output.threshold)
          : null;
        const restoredQuotes = Array.isArray(prodData.output.price_quotes)
          ? prodData.output.price_quotes.map(mapPriceQuote)
          : [];
        const restoredDecision = prodData.output.decision
          ? normalizeDecision(prodData.output.decision)
          : null;
        // Read the restored vendor (804) up front so the REAL
        // vendor_is_small drives the initial reachable-stage recompute —
        // a non-small stamped vendor keeps DD 2579 reachable on reload.
        // The persistent greying is independently driven by the live
        // live availability memo (which reacts to the vendor), so this
        // only affects the initial landing/maxReachable index.
        const r804recompute = await getStageData(flowId, 804);
        if (!isCurrentOperation()) return null;
        const restoredVendorForRecompute = r804recompute?.output?.award_vendor
          ? mapGpcVendor(r804recompute.output.award_vendor)
          : null;
        const restoredAwardIsSmall =
          typeof r804recompute?.output?.gpc_vendor_is_small === 'boolean'
            ? r804recompute.output.gpc_vendor_is_small
            : null;
        availableStages = applicableGpcFormStages({
          sourcingPath: restoredThreshold?.sourcingPath ?? null,
          dollarAmount: restoredThreshold?.total ?? prodData.output.unit_price ?? null,
          unitPrice: prodData.output.unit_price ?? null,
          buyType: restoredThreshold?.buyParams?.buy_type ?? null,
          sourceCount: restoredQuotes.filter(
            (q: GpcPriceQuote) => q.price !== null && q.price !== undefined,
          ).length,
          vendorIsSmall: restoredAwardIsSmall ?? restoredVendorForRecompute?.is_small_business ?? true,
          category: restoredDecision?.category ?? '',
          pilferable: false,
          taxable: null,
        });
      }
    }
    // Restore the per-form (S3b–S11) stage outputs (best-effort, mirror
    // the existing getStageData pattern). Codes (clean contiguous,
    // 2026-06-24): 803 naics_psc · 804 mandatory_source (+award vendor) ·
    // 805 quotes · 806 889 · 807 abstract · 808 dd2579 · 809 af_form9 ·
    // 810 ecars · 811 tax_exempt · 812 receipt · 813 iuid.
    // NAICS/PSC (803, both paths)
    const rNps = await getStageData(flowId, 803);
    if (!isCurrentOperation()) return null;
    if (rNps?.output?.naics_psc) onPatch({ naicsPsc: mapGpcNaicsPsc(rNps.output.naics_psc) });
    // Mandatory Source (804, Path B) — decision + scoped products + the
    // award vendor (folded in from the removed standalone vendor stage).
    const rMs = await getStageData(flowId, 804);
    if (!isCurrentOperation()) return null;
    onPatch({ carpetIiCheck: restoreGpcCarpetCheck(rMs?.output) });
    if (rMs?.output?.decision) onPatch({ decision: normalizeDecision(rMs.output.decision) });
    if (rMs?.output && Array.isArray(rMs.output.products)) {
      onPatch({ products: rMs.output.products as Product[] });
    }
    if (rMs?.output?.source_status && typeof rMs.output.source_status === 'object') {
      onPatch({ sourceStatus: rMs.output.source_status as Record<string, GpcSourceStatus> });
    }
    if (rMs?.output && Array.isArray(rMs.output.selected_product_ids)) {
      onPatch({ selectedProductIds: (rMs.output.selected_product_ids as unknown[]).map(String) });
    }
    // Restore the EXPLICIT award pick + override reason (FY26 award model)
    // alongside the competition set so the radio + Section E reason
    // rehydrate and the chosen vendor still routes to the forms.
    if (typeof rMs?.output?.award_product_id === 'string') {
      onPatch({ awardProductId: rMs.output.award_product_id });
    }
    if (typeof rMs?.output?.award_override_reason === 'string') {
      onPatch({ awardOverrideReason: rMs.output.award_override_reason });
    }
    if (rMs?.output?.award_vendor) {
      onPatch({ vendor: mapGpcVendor(rMs.output.award_vendor) });
    }
    if (typeof rMs?.output?.gpc_vendor_is_small === 'boolean') {
      onPatch({ awardIsSmall: rMs.output.gpc_vendor_is_small });
    }
    // Competition quotes (805, Path B)
    const rQuotes = await getStageData(flowId, 805);
    if (!isCurrentOperation()) return null;
    if (rQuotes?.output) {
      const cq = rQuotes.output.competition_quotes || rQuotes.output.price_quotes;
      if (Array.isArray(cq)) onPatch({ competitionQuotes: cq.map(mapPriceQuote) });
    }
    const r889 = await getStageData(flowId, 806);
    if (!isCurrentOperation()) return null;
    if (r889?.output?.rep_889) onPatch({ rep889: mapGpc889(r889.output.rep_889) });
    const rAbs = await getStageData(flowId, 807);
    if (!isCurrentOperation()) return null;
    const restoredAbstract = restoreGpcAbstractFromStageOutput(rAbs?.output);
    if (restoredAbstract) onPatch({ abstract: restoredAbstract });
    const rDd = await getStageData(flowId, 808);
    if (!isCurrentOperation()) return null;
    // A force-rerun prefill is persisted as a full canonical record in
    // `prefill_cache.result` alongside the prior saved form. Prefer it
    // on re-entry so navigation cannot resurrect old DD-2579 values.
    const restoredDd2579 = restoreGpcDd2579FromStageOutput(rDd?.output);
    if (restoredDd2579) onPatch({ dd2579: restoredDd2579 });
    const rAf9 = await getStageData(flowId, 809);
    if (!isCurrentOperation()) return null;
    if (rAf9?.output?.af_form9) onPatch({ afForm9: mapGpcAfForm9(rAf9.output.af_form9) });
    const rEc = await getStageData(flowId, 810);
    if (!isCurrentOperation()) return null;
    if (rEc?.output?.approval) onPatch({ approval: mapGpcApproval(rEc.output.approval) });
    const rTx = await getStageData(flowId, 811);
    if (!isCurrentOperation()) return null;
    if (rTx?.output?.tax_exempt) onPatch({ taxExempt: mapGpcTaxExempt(rTx.output.tax_exempt) });
    const rRcp = await getStageData(flowId, 812);
    if (!isCurrentOperation()) return null;
    onPatch({ receipt: restoreGpcReceiptFromStageOutput(rRcp?.output) });
    const rIu = await getStageData(flowId, 813);
    if (!isCurrentOperation()) return null;
    if (rIu?.output?.iuid) onPatch({ iuid: mapGpcIuid(rIu.output.iuid) });
  } catch (e) {
    if (!isCurrentOperation()) return null;
    hydrationPartial = true;
    console.warn('[App] Failed to restore GPC stage data:', e);
  }

  if (!isCurrentOperation()) return null;
  return {
    availableStages,
    hydrationPartial,
    ...resolveGpcLanding(
      priceSearchBlocksForms ? completedStages.filter((stage) => stage !== 802)
        : completedStages,
      availableStages, savedStage, stageHint,
    ),
  };
}
